/**
 * QueueScreen - The matchmaking queue UI component.
 *
 * Responsibilities:
 * - Display current queue status (players in queue, players needed)
 * - Provide Join Queue and Leave Queue actions
 * - React to backend queue events (QueueStatusUpdate)
 * - Transition to match screen when a match starts
 *
 * The QueueScreen reads state from AppShell (which holds the authoritative
 * GameStateService) and delegates player actions (join/leave) through AppShell
 * to the backend via WebSocket.
 */

import { AppShell, AppShellEvent } from '../AppShell';
import { QueueState } from '../services/GameStateService';
import {
  type QueueMode,
  QUEUE_MODES,
  QUEUE_MODE_LABELS,
  QUEUE_MODE_PLAYER_COUNTS,
} from '../../shared/models/QueueModes';

// ─── Queue Screen Render Data ───────────────────────────────────────────────

/**
 * Data exposed for rendering the queue screen UI.
 * The actual DOM/React rendering is the responsibility of the view layer;
 * this component provides the reactive data and action handlers.
 */
export interface QueueScreenRenderData {
  /** Whether the player is currently in a queue. */
  isInQueue: boolean;
  /** The queue the player is in ('fast' | 'long'), or null when not queued
   *  (also null on legacy status messages without the field). */
  queueMode: QueueMode | null;
  /** Number of players currently waiting in the player's own queue. */
  playersInQueue: number;
  /** Number of additional players needed in the player's own queue. */
  playersNeeded: number;
  /** Number of players waiting in the FAST queue (shown for both queues). */
  fastQueuePlayers: number;
  /** Number of players waiting in the LONG queue. */
  longQueuePlayers: number;
  /** Whether the player's queue is full and a match will start shortly. */
  isQueueFull: boolean;
  /**
   * Seconds until the match auto-starts with bots (authoritative server
   * countdown — present from 2 s after a join until the timer fires, the
   * rest of the 30 s window).
   * The view shows "Starting with Bots in: X seconds" while defined and
   * hides the hint when undefined (the first 2 s after a timer reset by a
   * new join, or the match already started).
   */
  botFillSecondsLeft?: number;
  /** Human-readable status message for display. */
  statusMessage: string;
  /** Whether the join button should be enabled. */
  canJoin: boolean;
  /** Whether the leave button should be enabled. */
  canLeave: boolean;
}

/**
 * Callback invoked when the render data changes.
 */
export type QueueScreenRenderCallback = (data: QueueScreenRenderData) => void;

/**
 * Callback invoked when the screen should be hidden (e.g., match started).
 */
export type QueueScreenHiddenCallback = () => void;

// ─── Queue Screen ───────────────────────────────────────────────────────────

export class QueueScreen {
  private _appShell: AppShell;
  private _renderCallbacks: QueueScreenRenderCallback[] = [];
  private _hiddenCallbacks: QueueScreenHiddenCallback[] = [];
  private _eventUnsubscribe: (() => void) | null = null;
  private _currentData: QueueScreenRenderData;
  private _isVisible: boolean = false;

  constructor(appShell: AppShell) {
    this._appShell = appShell;
    this._currentData = this._buildRenderData();
  }

  // ─── Public Accessors ────────────────────────────────────────────────────

  /** Returns the current render data (computed fresh from AppShell state). */
  get renderData(): QueueScreenRenderData {
    return this._buildRenderData();
  }

  /** Returns true if the screen is currently visible/active. */
  get isVisible(): boolean {
    return this._isVisible;
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  /**
   * Show the queue screen and start listening for state changes.
   */
  show(): void {
    if (this._isVisible) {
      return;
    }

    this._isVisible = true;

    // Listen for app shell events to update render data
    this._eventUnsubscribe = this._appShell.onEvent(
      this._onAppEvent.bind(this)
    );

    // Build initial render data from current state
    this._updateRenderData();
  }

  /**
   * Hide the queue screen and stop listening for state changes.
   */
  hide(): void {
    if (!this._isVisible) {
      return;
    }

    this._isVisible = false;

    if (this._eventUnsubscribe) {
      this._eventUnsubscribe();
      this._eventUnsubscribe = null;
    }
  }

  // ─── Render Callbacks ────────────────────────────────────────────────────

  /**
   * Subscribe to render data changes.
   * Returns an unsubscribe function.
   */
  onRenderChange(callback: QueueScreenRenderCallback): () => void {
    this._renderCallbacks.push(callback);
    return () => {
      const index = this._renderCallbacks.indexOf(callback);
      if (index !== -1) {
        this._renderCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to the screen being hidden (e.g., match started).
   * Returns an unsubscribe function.
   */
  onHidden(callback: QueueScreenHiddenCallback): () => void {
    this._hiddenCallbacks.push(callback);
    return () => {
      const index = this._hiddenCallbacks.indexOf(callback);
      if (index !== -1) {
        this._hiddenCallbacks.splice(index, 1);
      }
    };
  }

  // ─── Player Actions ──────────────────────────────────────────────────────

  /**
   * Request to join the matchmaking queue in the given MODE ('fast' — 4
   * players — or 'long' — 8 players). Delegates to AppShell.joinQueue(mode)
   * which sends a JoinQueueRequest to the backend (the server validates the
   * mode and enforces the one-queue-at-a-time rule). Returns true if the
   * message was sent.
   */
  joinQueue(mode: QueueMode = 'fast'): boolean {
    return this._appShell.joinQueue(mode);
  }

  /**
   * The queue modes the player can choose from (shared spec — display order
   * = QUEUE_MODES), with label + size, for the mode picker UI.
   */
  getQueueModeOptions(): Array<{
    mode: QueueMode;
    label: string;
    playerCount: number;
  }> {
    return QUEUE_MODES.map((mode) => ({
      mode,
      label: QUEUE_MODE_LABELS[mode],
      playerCount: QUEUE_MODE_PLAYER_COUNTS[mode],
    }));
  }

  /**
   * Request to leave the matchmaking queue.
   * Delegates to AppShell.leaveQueue() which sends a LeaveQueueRequest to the backend.
   * Returns true if the message was sent.
   */
  leaveQueue(): boolean {
    return this._appShell.leaveQueue();
  }

  // ─── Internal Methods ────────────────────────────────────────────────────

  private _onAppEvent(event: AppShellEvent): void {
    switch (event.type) {
      case 'stateChange': {
        // Update render data when game state changes
        this._updateRenderData();
        break;
      }
      case 'screenChange': {
        const data = event.data as { previousScreen: string; currentScreen: string };
        // If we've navigated away from the queue screen, hide ourselves
        if (data.currentScreen !== 'queue' && this._isVisible) {
          this.hide();
          this._notifyHidden();
        }
        break;
      }
      case 'connectionChange': {
        // If disconnected, update render data (buttons disabled)
        this._updateRenderData();
        break;
      }
    }
  }

  private _updateRenderData(): void {
    const newData = this._buildRenderData();
    this._currentData = newData;

    // Notify render callbacks
    for (const callback of this._renderCallbacks) {
      callback(newData);
    }
  }

  private _buildRenderData(): QueueScreenRenderData {
    const queueState = this._appShell.gameState.queue;
    const isConnected = this._appShell.connectionState === 'connected';

    const isInQueue = queueState.isInQueue;
    const queueMode = queueState.queueMode;
    const playersInQueue = queueState.playersInQueue;
    const playersNeeded = queueState.playersNeeded;
    const isQueueFull = isInQueue && playersNeeded <= 0;

    // Status message: while queued the box shows a single "Looking for
    // games…" line (no per-queue names/counts — the per-queue counters
    // were removed from the landing box). Not queued: "Choose a queue..."
    // (the landing hides the whole box while this text is active).
    let statusMessage: string;
    if (!isConnected) {
      statusMessage = 'Disconnected from server';
    } else if (!isInQueue) {
      statusMessage = 'Choose a queue...';
    } else {
      statusMessage = 'Looking for games…';
    }

    return {
      isInQueue,
      queueMode,
      playersInQueue,
      playersNeeded,
      fastQueuePlayers: queueState.fastQueuePlayers,
      longQueuePlayers: queueState.longQueuePlayers,
      isQueueFull,
      botFillSecondsLeft: queueState.botFillSecondsLeft,
      statusMessage,
      canJoin: isConnected && !isInQueue,
      canLeave: isConnected && isInQueue,
    };
  }

  private _notifyHidden(): void {
    for (const callback of this._hiddenCallbacks) {
      callback();
    }
  }
}
